import { Alert, Text } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import type { UserFriendlyError } from './utils/3dsErrorMessage';
import { isZuora3ds2Error, getUserFriendly3dsError } from './utils/3dsErrorMessage';

interface Props {
  errorMessage?: string;
  showRetry: boolean;
  retry: () => void;
}

const S = {
  TextWithLineBreak: styled(Text)`
    white-space: pre-line;
  `,
};

export const ErrorMessage = ({ errorMessage, showRetry, retry }: Props): JSX.Element | null => {
  const { t } = useTranslation('checkout', { keyPrefix: 'payment-details' });
  const [error, setError] = useState<UserFriendlyError | undefined>();

  useEffect(() => {
    if (errorMessage === undefined) {
      setError(undefined);
      return;
    }

    if (isZuora3ds2Error(errorMessage)) {
      const userFriendly3dsError = getUserFriendly3dsError(errorMessage);
      setError(userFriendly3dsError);
    } else {
      setError({ userFriendlyZuoraMessage: errorMessage });
    }
  }, [errorMessage]);

  if (error === undefined) {
    return null;
  }

  const hasUserFriendlyZuoraMessage = error.userFriendlyZuoraMessage !== undefined;
  const showZuoraMessageAsTitle =
    error.messageTranslationKey !== undefined && hasUserFriendlyZuoraMessage;

  return (
    <Alert
      actionText={showRetry ? t('retry') : undefined}
      onAction={retry}
      kind="danger"
      title={showZuoraMessageAsTitle ? error.userFriendlyZuoraMessage : undefined}
    >
      {!showZuoraMessageAsTitle && hasUserFriendlyZuoraMessage && (
        <S.TextWithLineBreak>{error.userFriendlyZuoraMessage}</S.TextWithLineBreak>
      )}

      {error.messageTranslationKey !== undefined && (
        <S.TextWithLineBreak>{t(error.messageTranslationKey)}</S.TextWithLineBreak>
      )}
    </Alert>
  );
};
